import SVG, { Box } from 'svg.js';
import eventbus from 'tiny-emitter/instance';
import Matrix from './Matrix';
import Point, { point } from './Point';
import { useState } from '@/store/useStore';
import { MODE, RENDERER_MODE } from '@/constants';
import { reactive, watch } from 'vue';
import Application from './application';

interface Options {
  zoomFactor?: number;
  wheelZoom?: boolean;
  pinchZoom?: boolean;
  panning?: boolean;
  panButtons?: number[];
  withCtrlButtons?: number[];
  wheelWithCtrl?: boolean;
  wheelPanning?: boolean;
  oneFingerPan?: boolean;
  margins?: boolean;
  wheelZoomDeltaModeLinePixels?: number;
  wheelZoomDeltaModeScreenPixels?: number;
  panAmount?: number;
  showCoordinates?: boolean;
}

const settings = reactive(useState('settings'));
const camera = reactive(useState('camera'));

const normalizeEvent = (ev: TouchEvent | MouseEvent): Array<{ clientX: number; clientY: number }> =>
  'touches' in ev
    ? Array.from(ev.touches).map((t) => ({ clientX: t.clientX, clientY: t.clientY }))
    : [{ clientX: ev.clientX, clientY: ev.clientY }];

const withMeta = (ev: KeyboardEvent | WheelEvent | MouseEvent): boolean => ev.ctrlKey || ev.metaKey;

const keyHandlers = {
  '+': (ev: KeyboardEvent, o: ZoomManager) => o.zoomIn(),
  '-': (ev: KeyboardEvent, o: ZoomManager) => o.zoomOut(),
  0: (ev: KeyboardEvent, o: ZoomManager) => o.zoomAndCenterBox(),
  ArrowUp: (ev: KeyboardEvent, o: ZoomManager) => (withMeta(ev) ? o.zoomIn() : o.panUp()),
  ArrowDown: (ev: KeyboardEvent, o: ZoomManager) => (withMeta(ev) ? o.zoomOut() : o.panDown()),
  ArrowLeft: (ev: KeyboardEvent, o: ZoomManager) => o.panLeft(),
  ArrowRight: (ev: KeyboardEvent, o: ZoomManager) => o.panRight(),
  NumpadAdd: (ev: KeyboardEvent, o: ZoomManager) => o.zoomIn(),
  NumpadSubtract: (ev: KeyboardEvent, o: ZoomManager) => o.zoomOut(),
} as const;

type AvailableKeys = keyof typeof keyHandlers;

export class ZoomManager {
  svg: SVG.Doc;
  canvas: SVG.G;
  lastP: PointLike | null = null;
  lastTouches: Array<{ clientX: number; clientY: number }> = [];
  zoomInProgress = false;
  wasPanning = false;

  zoomFactor = 0.1;
  zoomMin = Number.MIN_VALUE;
  zoomMax = Number.MAX_VALUE;
  doWheelZoom = true;
  doPinchZoom = true;
  doPanning = true;
  panButtons = [1];
  withCtrlButtons: Array<number> = [];
  wheelWithCtrl = false;
  wheelPanning = false;
  oneFingerPan = false;
  margins = false;
  wheelZoomDeltaModeLinePixels = 17;
  wheelZoomDeltaModeScreenPixels = 53;
  panAmount = 20;
  showCoordinates = false;

  oldViewBox: BoxLike | null = null;
  defaultZoom = 1;
  revealSeatLvl = 1.5;
  wasPanningTimeout: number | undefined = undefined;

  constructor(
    public app: Application,
    options: Options = {}
  ) {
    this.app = app;
    this.svg = app.svg;
    this.canvas = app.canvas;

    this.initSizeAndViewBox();

    SVG.on(window, 'resize', this.handleWindowResize.bind(this));
    SVG.on(window, 'keydown', this.handleKeyDown.bind(this));

    eventbus.on('camera:panned', this.updateGridAndAxis, this);
    eventbus.on('camera:zoomed', this.updateGridAndAxis, this);

    eventbus.on('seatmap:mode:changed', this.updateOptions, this);

    eventbus.on('grid:show', this.showHideGrid.bind(this, true), this);
    eventbus.on('grid:hide', this.showHideGrid.bind(this, false), this);

    this.updateOptions(options);
    this.installWatchers();
  }

  updateOptions(options: Options) {
    this.updateZoomEvents({
      ...options,
      panButtons: settings.mode !== MODE.RENDERER ? [1] : [0, 1],
      // withCtrlButtons: settings.mode !== MODE.RENDERER ? [0] : [],
      wheelWithCtrl: settings.mode !== MODE.RENDERER,
      wheelPanning: settings.mode !== MODE.RENDERER,
      oneFingerPan: true,
      // zoomMin: 0.25,
      // zoomMax: 3,
    });
  }

  updateZoomEvents(options: Options) {
    this.zoomFactor = options.zoomFactor ?? 0.1;
    // this.zoomMin = options.zoomMin ?? Number.MIN_VALUE;
    // this.zoomMax = options.zoomMax ?? Number.MAX_VALUE;
    this.doWheelZoom = options.wheelZoom ?? true;
    this.doPinchZoom = options.pinchZoom ?? true;
    this.doPanning = options.panning ?? true;
    this.panButtons = options.panButtons ?? [1];
    this.withCtrlButtons = options.withCtrlButtons ?? [];
    this.wheelWithCtrl = options.wheelWithCtrl ?? false;
    this.wheelPanning = options.wheelPanning ?? false;
    this.oneFingerPan = options.oneFingerPan ?? false;
    this.margins = options.margins ?? false;
    this.wheelZoomDeltaModeLinePixels = options.wheelZoomDeltaModeLinePixels ?? 17;
    this.wheelZoomDeltaModeScreenPixels = options.wheelZoomDeltaModeScreenPixels ?? 53;
    this.panAmount = options.panAmount ?? 20;
    this.showCoordinates = options.showCoordinates ?? false;

    this.svg.off('.panZoom');
    SVG.off(document, '.panZoom');

    if (this.app.props.disablePanZoom) return;

    if (this.doWheelZoom) {
      this.svg.on('wheel.panZoom', this.wheelZoom, this, { passive: false });
    }

    if (this.doPinchZoom) {
      this.svg.on('touchstart.panZoom', this.pinchZoomStart, this, { passive: false });
    }

    if (this.doPanning && !this.app.isIOS && !this.app.isAndroid) {
      this.svg.on('mousedown.panZoom', this.panStart, this, { passive: false });
    }

    if (this.wheelPanning) {
      SVG.on(document, 'wheel.panZoom', this.wheelPan, this, { passive: false });
    }
  }

  initSizeAndViewBox() {
    let { width, height } = this.svg.rbox();
    this.svg.size(width, height);

    width *= 2;
    height *= 2;

    const w2 = width / 2;
    const h2 = height / 2;
    this.svg.viewbox(-w2, -h2, width, height);
    // this.zoom(0.5);
    this.updateGridAndAxis({ x: -w2, y: -h2, width, height });

    this.oldViewBox = this.svg.viewbox();
  }

  installWatchers() {
    watch(() => camera.showGrid, this.showHideGrid.bind(this));
  }

  pan(dx: number, dy: number) {
    if (this.app.props.disablePanZoom) return;
    const box = this.svg.viewbox();
    box.x -= dx;
    box.y -= dy;
    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  centerPoint(x: number, y: number) {
    if (this.app.props.disablePanZoom) return;
    const box = this.svg.viewbox();
    const zoom = this.zoom();
    const centerX = box.width / 2 / zoom;
    const centerY = box.height / 2 / zoom;
    box.x = x - centerX;
    box.y = y - centerY;
    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  centerBox(box: BoxLike) {
    const viewbox = this.svg.viewbox();

    const bcx = box.x + box.width / 2;
    const bcy = box.y + box.height / 2;

    const vcx = viewbox.x + viewbox.width / 2;
    const vcy = viewbox.y + viewbox.height / 2;

    const dx = bcx - vcx;
    const dy = bcy - vcy;

    viewbox.x += dx;
    viewbox.y += dy;

    this.svg.viewbox(viewbox);
    return viewbox;
  }

  // zooms to the given box, but leaves space around it
  zoomAndCenterBox(box = this.canvas.rbox(this.svg), marginFactor = 0.05) {
    if (this.app.props.disablePanZoom) return;
    if (!box.width) return;
    if (this.wasPanning) return;

    return new Promise<BoxLike>((resolve) => {
      // set the new viewbox
      const newBox = this.adaptAspectRatio(this.addMargin(box, marginFactor));
      this.svg
        .animate(200, '>')
        .viewbox(newBox.x, newBox.y, newBox.width, newBox.height)
        .afterAll(() => {
          eventbus.emit('camera:zoomed', newBox);
          resolve(newBox);
        });
    });
  }

  centerizeAndInitializeZoomLevels(reInit = false) {
    const box = this.canvas.rbox(this.svg);
    const marginFactor = 0.05;
    if (!box.width) return;

    const viewbox = this.svg.viewbox();

    const needsFitting = viewbox.width < box.width || viewbox.height < box.height || RENDERER_MODE;

    let newBox: BoxLike;
    if (needsFitting) {
      newBox = this.adaptAspectRatio(this.addMargin(box, marginFactor));
      this.svg.viewbox(newBox);
    } else {
      newBox = this.centerBox(box);
    }

    this.defaultZoom = this.zoom();

    let max = 12;
    if (this.app.isMobileDevice) {
      max = 22;
    }
    this.zoomMin = this.defaultZoom / 2;
    this.zoomMax = this.defaultZoom * max;

    this.revealSeatLvl = this.app.props.revealSectionsMode === 'self' ? this.defaultZoom : this.defaultZoom * 1.5;

    // nice appear animation for seatmap
    if (RENDERER_MODE) {
      if (!reInit) {
        this.svg.style({
          opacity: 0,
          transform: 'scale(3)',
        });
        setTimeout(() => {
          this.svg.style({
            transition: 'all 0.3s ease-out',
            transform: 'scale(1)',
            opacity: 1,
          });
          setTimeout(() => {
            this.svg.attr('style', null);
            eventbus.emit('camera:panned', newBox);
          }, 300);
        }, 0);
      }
    } else {
      eventbus.emit('camera:panned', newBox);
    }
  }

  adaptAspectRatio(box: BoxLike) {
    const viewbox = this.svg.viewbox();
    const viewboxAspect = viewbox.width / viewbox.height;
    const boxAspect = box.width / box.height;
    if (boxAspect < viewboxAspect) {
      const width = box.height * viewboxAspect;
      box.x -= (width - box.width) / 2;
      box.width = width;
    } else {
      const height = box.width / viewboxAspect;
      box.y -= (height - box.height) / 2;
      box.height = height;
    }
    return box;
  }

  addMargin(box: BoxLike, marginFactor = 0.05) {
    const margin = marginFactor * Math.max(box.width, box.height);

    box.width += 2 * margin;
    box.height += 2 * margin;
    box.x -= margin;
    box.y -= margin;
    return box;
  }

  panUp(distance = this.panAmount) {
    if (this.app.props.disablePanZoom) return;
    const box = this.svg.viewbox();
    box.y -= distance / this.zoom();
    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  panDown(distance = this.panAmount) {
    if (this.app.props.disablePanZoom) return;
    const box = this.svg.viewbox();
    box.y += distance / this.zoom();
    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  panLeft(distance = this.panAmount) {
    if (this.app.props.disablePanZoom) return;
    const box = this.svg.viewbox();
    box.x -= distance / this.zoom();
    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  panRight(distance = this.panAmount) {
    if (this.app.props.disablePanZoom) return;
    const box = this.svg.viewbox();
    box.x += distance / this.zoom();
    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  handleWindowResize() {
    const { width, height } = this.svg.rbox();
    const box = this.svg.viewbox();
    const aspectRatio = box.width / box.height;

    if (width / height > aspectRatio) {
      box.width = box.height * (width / height);
    } else {
      box.height = box.width * (height / width);
    }

    this.svg.viewbox(box);
    eventbus.emit('camera:panned', box);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (this.app.props.disablePanZoom) return;
    if (keyHandlers[event.key as AvailableKeys] && !this.app.seatmap.isHotkeysDisabled(event)) {
      // If an item is selected, prevent arrow keys from scrolling the page, move the selection instead
      if (event.code.includes('Arrow') && this.app.seatmap.selection.items.length > 0) {
        return;
      }

      event.preventDefault();
      keyHandlers[event.key as AvailableKeys](event, this);
    }
  }

  calcZoomStep(step: number) {
    return Math.pow(1 + this.zoomFactor, -1 * step) * this.zoom();
  }

  wheelZoom(ev: WheelEvent) {
    if (this.wheelWithCtrl && !withMeta(ev)) return;
    ev.preventDefault();

    // When wheeling on a mouse,
    // - chrome by default uses deltaY = 53, deltaMode = 0 (pixel)
    // - firefox by default uses deltaY = 3, deltaMode = 1 (line)
    // - chrome and firefox on windows after configuring "One screen at a time"
    //   use deltaY = 1, deltaMode = 2 (screen)
    //
    // Note that when when wheeling on a touchpad, deltaY depends on how fast
    // you swipe, but the deltaMode is still different between the browsers.
    //
    // Normalize everything so that zooming speed is approximately the same in all cases
    let normalizedPixelDeltaY;
    switch (ev.deltaMode) {
      case 1:
        normalizedPixelDeltaY = ev.deltaY * this.wheelZoomDeltaModeLinePixels;
        break;
      case 2:
        normalizedPixelDeltaY = ev.deltaY * this.wheelZoomDeltaModeScreenPixels;
        break;
      default:
        // 0 (already pixels) or new mode (avoid crashing)
        normalizedPixelDeltaY = ev.deltaY;
        break;
    }

    let lvl = this.calcZoomStep(normalizedPixelDeltaY / 100);
    const p = this.point(ev.clientX, ev.clientY);

    if (lvl > this.zoomMax) {
      lvl = this.zoomMax;
    }

    if (lvl < this.zoomMin) {
      lvl = this.zoomMin;
    }

    // if (this.dispatch('zoom', { level: lvl, focus: p }).defaultPrevented) {
    //   return this;
    // }

    this.zoom(lvl, p);
  }

  point(x: number, y: number) {
    return point.call(this.svg, x, y);
  }

  pinchZoomStart(ev: TouchEvent) {
    this.lastTouches = normalizeEvent(ev);

    // Start panning in case only one touch is found
    if (this.lastTouches.length < 2) {
      if (this.doPanning && this.oneFingerPan) {
        this.panStart.call(this, ev as unknown as MouseEvent);
      }
      return;
    }

    // Stop panning for more than one touch
    if (this.doPanning && this.oneFingerPan) {
      this.panStop();
    }

    // We call it so late, so the user is still able to scroll / reload the page via gesture
    // In case oneFingerPan is not active
    ev.preventDefault();

    // if (this.dispatch('pinchZoomStart', { event: ev }).defaultPrevented) {
    //   return;
    // }

    SVG.off(this.svg, 'touchstart.panZoom', this.pinchZoomStart, { passive: false });

    this.zoomInProgress = true;
    SVG.on(document, 'touchmove.panZoom', this.pinchZoom, this, { passive: false });
    SVG.on(document, 'touchend.panZoom', this.pinchZoomStop, this, { passive: false });
  }

  pinchZoomStop(ev: TouchEvent) {
    ev.preventDefault();

    const currentTouches = normalizeEvent(ev);
    if (currentTouches.length > 1) {
      return;
    }

    this.zoomInProgress = false;

    // this.dispatch('pinchZoomEnd', { event: ev });

    SVG.off(document, 'touchmove.panZoom', this.pinchZoom, { passive: false });
    SVG.off(document, 'touchend.panZoom', this.pinchZoomStop, { passive: false });
    this.svg.on('touchstart.panZoom', this.pinchZoomStart, this, { passive: false });
    eventbus.emit('camera:panned', this.svg.viewbox());

    if (currentTouches.length && this.doPanning && this.oneFingerPan) {
      this.panStart.call(this, ev as unknown as MouseEvent);
    }
  }

  pinchZoom(ev: TouchEvent) {
    ev.preventDefault();

    const currentTouches = normalizeEvent(ev);

    if (currentTouches.length < 2) {
      return;
    }

    const zoom = this.zoom();

    // Distance Formula
    const lastDelta = Math.sqrt(
      Math.pow(this.lastTouches[0].clientX - this.lastTouches[1].clientX, 2) +
        Math.pow(this.lastTouches[0].clientY - this.lastTouches[1].clientY, 2)
    );

    const currentDelta = Math.sqrt(
      Math.pow(currentTouches[0].clientX - currentTouches[1].clientX, 2) +
        Math.pow(currentTouches[0].clientY - currentTouches[1].clientY, 2)
    );

    let zoomAmount = lastDelta / currentDelta;

    if ((zoom < this.zoomMin && zoomAmount > 1) || (zoom > this.zoomMax && zoomAmount < 1)) {
      zoomAmount = 1;
    }

    const currentFocus = {
      x: currentTouches[0].clientX + 0.5 * (currentTouches[1].clientX - currentTouches[0].clientX),
      y: currentTouches[0].clientY + 0.5 * (currentTouches[1].clientY - currentTouches[0].clientY),
    };

    const lastFocus = {
      x: this.lastTouches[0].clientX + 0.5 * (this.lastTouches[1].clientX - this.lastTouches[0].clientX),
      y: this.lastTouches[0].clientY + 0.5 * (this.lastTouches[1].clientY - this.lastTouches[0].clientY),
    };

    const p = this.point(currentFocus.x, currentFocus.y);
    const focusP = this.point(2 * currentFocus.x - lastFocus.x, 2 * currentFocus.y - lastFocus.y);
    const box = new SVG.Box(this.svg.viewbox()).transform(
      new Matrix().translate(-focusP.x, -focusP.y).scale(zoomAmount).translate(p.x, p.y) as unknown as SVG.Matrix
    );

    this.svg.viewbox(box);

    this.lastTouches = currentTouches;

    eventbus.emit('camera:zoomed', box);

    // this.dispatch('zoom', { box: box, focus: focusP });
  }

  panStart(ev: MouseEvent) {
    const isMouse = ev.type.indexOf('mouse') > -1;

    // In case panStart is called with touch, ev.button is undefined
    if (
      isMouse &&
      !this.panButtons.includes(ev.button) &&
      (!withMeta(ev) || !this.withCtrlButtons.includes(ev.button))
    ) {
      return;
    }

    // ev.preventDefault();

    if (this.zoomInProgress) return;

    SVG.off(this.svg, 'mousedown.panZoom', this.panStart, { passive: false });

    this.lastTouches = normalizeEvent(ev);

    // this.dispatch('panStart', { event: ev });

    this.lastP = { x: this.lastTouches[0].clientX, y: this.lastTouches[0].clientY };

    SVG.on(document, 'touchmove.panZoom mousemove.panZoom', this.panning, this, {
      passive: false,
    });

    SVG.on(document, 'touchend.panZoom mouseup.panZoom', this.panStop, this, {
      passive: false,
    });
  }

  panStop() {
    // ev.preventDefault();

    SVG.off(document, 'touchmove.panZoom mousemove.panZoom', this.panning, { passive: false });
    SVG.off(document, 'touchend.panZoom mouseup.panZoom', this.panStop, { passive: false });
    // this.svg.on('mousedown.panZoom', this.panStart);

    if (!this.app.isIOS && !this.app.isAndroid) {
      this.svg.on('mousedown.panZoom', this.panStart, this, { passive: false });
    }

    if (this.wasPanning) {
      eventbus.emit('camera:panned', this.svg.viewbox());
    }

    clearTimeout(this.wasPanningTimeout);
    this.wasPanningTimeout = setTimeout(() => {
      this.wasPanning = false;
    }, 100);

    // this.dispatch('panEnd', { event: ev });
  }

  panning(ev: MouseEvent) {
    ev.preventDefault();

    const currentTouches = normalizeEvent(ev);

    const currentP = {
      x: currentTouches[0].clientX,
      y: currentTouches[0].clientY,
    };

    const p1 = this.point(currentP.x, currentP.y);

    const p2 = this.point(this.lastP!.x, this.lastP!.y);

    const deltaP = [p2.x - p1.x, p2.y - p1.y];

    if (!deltaP[0] && !deltaP[1]) {
      return;
    }

    const box = new SVG.Box(this.svg.viewbox()).transform(
      new Matrix().translate(deltaP[0], deltaP[1]) as unknown as SVG.Matrix
    );

    this.lastP = currentP;

    // if (this.dispatch('panning', { box, event: ev }).defaultPrevented) {
    //   return;
    // }

    this.svg.viewbox(box);
    this.wasPanning = true;
  }

  wheelPan(ev: WheelEvent) {
    if (withMeta(ev)) return;
    ev.preventDefault();

    const x = ev.shiftKey ? ev.deltaY : ev.deltaX;
    const y = ev.shiftKey ? ev.deltaX : ev.deltaY;

    this.pan(-x, -y);
  }

  zoom(level: number, point?: PointLike): undefined;
  zoom(): number;
  zoom(level?: number, point?: PointLike) {
    const box = this.calculateZoomBox(level, point);
    if (level == null) return box;
    if (this.app.props.disablePanZoom) return;
    this.svg.viewbox(box as Box);
    eventbus.emit('camera:zoomed', box);
  }

  calculateZoomBox(level: number, point?: PointLike): SVG.Box;
  calculateZoomBox(): number;
  calculateZoomBox(level?: number, point?: PointLike): SVG.Box | number;
  calculateZoomBox(level?: number, point?: PointLike) {
    const width = this.svg.width();
    const height = this.svg.height();

    const v = this.svg.viewbox();

    const zoomX = width / v.width;
    const zoomY = height / v.height;
    const zoom = Math.min(zoomX, zoomY);

    if (level == null) {
      return zoom;
    }

    // This was moved from wheelZoom to always adhere to the zoomMin and zoomMax
    if (level > this.zoomMax) {
      level = this.zoomMax;
    }

    if (level < this.zoomMin) {
      level = this.zoomMin;
    }

    let zoomAmount = zoom / level;

    // Set the zoomAmount to the highest value which is safe to process and recover from
    // The * 100 is a bit of wiggle room for the matrix transformation
    if (zoomAmount === Infinity) zoomAmount = Number.MAX_SAFE_INTEGER / 100;

    point = point || new Point(width / 2 / zoomX + v.x, height / 2 / zoomY + v.y);

    // @ts-expect-error wrong typings
    const box = new SVG.Box(v).transform(new Matrix({ scale: zoomAmount, origin: point }));
    return box;
  }

  animateZoom(level: number, point?: PointLike) {
    if (this.app.props.disablePanZoom) return;
    const box = this.calculateZoomBox(level, point);
    this.svg
      .animate(200, '>')
      .viewbox(box.x, box.y, box.width, box.height)
      .afterAll(() => {
        eventbus.emit('camera:zoomed', box);
      });
  }

  zoomIn(steps = 1) {
    if (RENDERER_MODE) {
      this.animateZoom(this.calcZoomStep(-steps));
    } else {
      this.zoom(this.calcZoomStep(-1));
    }
  }

  zoomOut(steps = 1) {
    if (RENDERER_MODE) {
      this.animateZoom(this.calcZoomStep(steps));
    } else {
      this.zoom(this.calcZoomStep(1));
    }
  }

  updateGrid({ x, y, width, height }: BoxLike) {
    if (RENDERER_MODE) return;
    if (!this.app.grid) {
      this.app.grid = this.svg
        .rect(width, height)
        .attr({
          fill: this.app.getGridPattern(),
          x,
          y,
        })
        .addClass('grid')
        .back()
        .style('pointer-events', 'none');
    } else {
      // resize the pattern so that the next magitute is shown when zoomed out enough
      // const magitute = Math.pow(10, Math.floor(Math.log10(this.zoom() * 2)));
      // this.app.gridPattern.scale(1 / magitute);

      this.app.grid?.attr({
        x,
        y,
        width,
        height,
      });
    }
  }

  updateCoordinateAxis({ width, height }: { width: number; height: number }) {
    if (!this.app.coordinates) {
      const group = this.svg.group().addClass('coordinates');
      group.add(this.svg.circle(5).addClass('origin').center(0, 0));
      group.add(this.svg.line(-width, 0, width, 0).addClass('x-axis').stroke({ width: 1 }));
      group.add(this.svg.line(0, -height, 0, height).addClass('y-axis').stroke({ width: 1 }));
      this.app.coordinates = group;
    } else {
      (this.app.coordinates.get(1) as SVG.Line).plot(-width, 0, width, 0);
      (this.app.coordinates.get(2) as SVG.Line).plot(0, -height, 0, height);
    }
  }

  updateGridAndAxis(box: BoxLike) {
    this.updateGrid(box);

    if (this.showCoordinates) {
      this.updateCoordinateAxis(box);
    }
  }

  showHideGrid(show: boolean) {
    if (show) {
      this.app.grid?.show();
    } else {
      this.app.grid?.hide();
    }
  }

  saveZoom() {
    this.oldViewBox = this.svg.viewbox();
  }

  restoreZoom() {
    const { x, y, width, height } = this.oldViewBox!;
    this.svg
      .animate(200, '>')
      .viewbox(x, y, width, height)
      .afterAll(() => {
        eventbus.emit('camera:zoomed', this.oldViewBox);
      });
  }
}
